import React from 'react';
import { useIntl } from 'react-intl';
import { Box, useTheme } from '@mui/material';
import { useAtomValue } from 'jotai';
import { ParentSize } from '@visx/responsive';
import { FullSizeCenteredFlexBox } from '../../../Components/FlexBox';
import BarChart from '../../../Components/Charts/BarChart';
import { themeAtom } from '../../../Atoms';
import TooltipComponent from './TooltipComponent';
import ContentError from '../../../Components/Error/ContentError';

const RedemptionGraphWidget = function RedemptionGraphWidget({
  redemptionData = [],
  enableScroll = false,
  noData = false,
  minimumRedemptionCalls = null,
}) {
  const intl = useIntl();
  const theme = useTheme();
  const currentTheme = useAtomValue(themeAtom);

  const keys = redemptionData?.map(({ entity }) => entity);

  const redemption = redemptionData
    ?.sort((a, b) => {
      const aValue =
        a?.practices && a?.answeredNum
          ? (a?.practices / a?.answeredNum) * 100
          : 0;
      const bValue =
        b?.practices && b?.answeredNum
          ? (b?.practices / b?.answeredNum) * 100
          : 0;
      return bValue - aValue;
    })
    ?.map((d, index) => ({
      index: index + 1,
      label: d?.entity,
      [d?.entity]:
        d?.practices && d?.answeredNum
          ? (d?.practices / d?.answeredNum) * 100
          : 0,
      target:
        d?.totalPractices && d?.totalAnsweredNum
          ? (d?.totalPractices / d?.totalAnsweredNum) * 100
          : 0,
      details: d,
      minimumReached: minimumRedemptionCalls
        ? d?.answeredNum >= minimumRedemptionCalls
        : true,
    }));

  return (
    <>
      <Box
        sx={{
          width: "auto",
          height: "40vh"
        }}>
        {noData ? (
          <FullSizeCenteredFlexBox justifyContent="center">
            <ContentError text={intl.messages.NO_PRACTICES_AVAILABLE} />
          </FullSizeCenteredFlexBox>
        ) : (
          <ParentSize>
            {(parent) => (
              //parent component passes his css properties to BarChart
              (<BarChart
                width={parent.width}
                height={parent.height}
                dataToShow={[redemption]}
                keys={keys}
                colors={theme.custom
                  .generateConsistentUniqueColors(keys)
                  ?.map((x) => x[currentTheme])}
                // showTarget
                universalTargetLineValue={
                  redemption.length > 0 ? redemption[0]?.target : undefined
                }
                yTickFormat={(v) => `${v.toFixed(2)}%`}
                minTickWidth={enableScroll ? 180 : undefined}
                renderTargetIcon={({ x, y, index, value, target, data }) => {
                  const size = 30;
                  return (
                    <text
                      key={`percentage-${index}`}
                      x={x}
                      y={y}
                      textAnchor="middle"
                      fontSize={size}
                      fontWeight="bold"
                      style={{
                        opacity:
                          data?.minimumReached ||
                          data?.minimumReached === undefined
                            ? 1
                            : 0.6,
                      }}
                      fill={
                        data?.minimumReached ||
                        data?.minimumReached === undefined
                          ? value < target
                            ? theme.palette.error.main
                            : theme.palette.success.main
                          : '#b3b3b3'
                      }
                    >
                      {`${value?.toFixed(2)} %`}
                    </text>
                  );
                }}
                TooltipContent={TooltipComponent}
              />)
            )}
          </ParentSize>
        )}
      </Box>
    </>
  );
};

export default RedemptionGraphWidget;
